import { useState, useMemo, type ReactNode } from "react";
import {
  LinkBackwardIcon,
  Download04Icon,
  ArrowUp01Icon,
  ArrowDown01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import {
  AlertCircleIcon,
  LinkBackwardIcon as LinkBackwardSolidIcon
} from "@hugeicons-pro/core-solid-rounded";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "shared/components/Popover";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Button,
  ButtonVariant,
  ButtonSize,
  Show
} from "shared/components";
import { cx } from "shared/utils";
import { EmailFrame } from "shared/components/EmailFrame";
import { AddressChip } from "./AddressChip";
import { LinkifiedText } from "shared/utils/linkify-text";
import { MessageContentCard } from "./MessageContentCard";
import { TranslateIncomingToggle } from "./TranslateIncomingToggle";
import { useMessageTranslation } from "../hooks/useMessageTranslation";
import { useInboxEmbed } from "../context/useInboxEmbed";
import { stripMarkupForText } from "../utils/messageSnippet";
import { type Attachment, type ReplyTarget } from "../types";

// CRM-1289: emails collapse to ~2 lines by default with "Show more."
const BODY_COLLAPSE_THRESHOLD = 120;
// The pop-up collapses to the design's 3-line text preview much earlier.
const COMPACT_COLLAPSE_THRESHOLD = 160;

/**
 * Replace cid: references in HTML with actual attachment URLs.
 * - If the attachment URL is available, replace cid: with the real URL.
 * - If the attachment exists but URL is empty (upload pending), show a
 *   loading placeholder so the image appears once the upload finishes.
 * - If no matching attachment exists (e.g. from quoted replies), remove
 *   the image tag entirely.
 */
const replaceInlineImages = (
  html: string,
  attachments: Attachment[]
): string => {
  const inlineMap = new Map<string, string>();
  for (const att of attachments)
    if (att.is_inline && att.content_id)
      inlineMap.set(att.content_id, att.file_url);

  return html.replace(/<img[^>]*src="cid:([^"]+)"[^>]*>/gi, (imgTag, cid) => {
    if (!inlineMap.has(cid))
      // No matching attachment at all — remove (e.g. from quoted replies)
      return "";

    const url = inlineMap.get(cid)!;
    if (url) return imgTag.replace(`src="cid:${cid}"`, `src="${url}"`);

    // Attachment exists but file is still uploading — show placeholder
    return `<div style="display:inline-block;background:#f0f0f0;border-radius:8px;padding:16px 24px;color:#888;font-size:12px;margin:4px 0;">Loading image...</div>`;
  });
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const resolveDisplaySubject = (
  subject: string | undefined,
  showTranslated: boolean,
  translatedSubject: string | null
): string | undefined =>
  showTranslated && translatedSubject !== null ? translatedSubject : subject;

interface AddressFieldProps {
  label: string;
  address: string;
  tinted?: boolean;
}

const AddressField = ({ label, address, tinted }: AddressFieldProps) => (
  <div className="flex min-w-0 shrink items-center gap-2">
    <span className="shrink-0 text-xs font-medium text-content-1000">
      {label}
    </span>
    <AddressChip address={address} tinted={tinted} />
  </div>
);

// Collapse long recipient lists behind a "+N" chip that expands on click.
const RECIPIENTS_VISIBLE_LIMIT = 3;

const AddressList = ({
  label,
  addresses,
  tinted
}: {
  label: string;
  addresses: string[];
  tinted?: boolean;
}) => {
  const [expanded, setExpanded] = useState(false);

  if (addresses.length === 0) return null;

  const overflow = addresses.length - RECIPIENTS_VISIBLE_LIMIT;
  const visible =
    expanded || overflow <= 0
      ? addresses
      : addresses.slice(0, RECIPIENTS_VISIBLE_LIMIT);

  return (
    <div className="flex min-w-0 items-center gap-2">
      <span className="shrink-0 text-xs font-medium text-content-1000">
        {label}
      </span>
      <span className="flex min-w-0 flex-wrap items-center gap-1.5">
        {visible.map((entry) => (
          <AddressChip key={entry} address={entry} tinted={tinted} />
        ))}
        {!expanded && overflow > 0 ? (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="shrink-0 text-xs font-medium text-content-200 hover:text-content-1000"
            aria-label={`Show ${overflow} more recipient${overflow > 1 ? "s" : ""}`}
          >
            +{overflow}
          </button>
        ) : null}
      </span>
    </div>
  );
};

// Pop-up From/To rows (2152:17512): floating stacks name over address, the
// drawer keeps one "name • address" line capped at the design's 300px.
const CompactAddressRow = ({
  label,
  name,
  address,
  inline,
  overflowCount = 0,
  trailing
}: {
  label: string;
  name?: string;
  address?: string;
  inline: boolean;
  overflowCount?: number;
  trailing?: ReactNode;
}) => {
  if (!name && !address) return null;
  // A name that is just the address again adds nothing.
  const displayName = name !== address ? name : undefined;
  const suffix = overflowCount > 0 ? ` +${overflowCount}` : "";
  const labelNode = (
    <span className="w-8 shrink-0 font-medium text-content-1000">{label}</span>
  );
  const nameNode = displayName ? (
    <span className={cx("truncate", inline ? "max-w-[300px]" : "")}>
      {displayName}
    </span>
  ) : null;

  if (inline)
    return (
      <div className="flex items-center gap-1">
        {labelNode}
        <span className="flex min-w-0 items-center gap-1 font-medium text-content-1000">
          {nameNode}
          <Show if={Boolean(displayName && address)}>
            <span className="size-0.5 shrink-0 rounded-full bg-content-100" />
          </Show>
          <Show if={Boolean(address)}>
            <span className="max-w-[300px] truncate">
              {address}
              {suffix}
            </span>
          </Show>
          {trailing}
        </span>
      </div>
    );

  return (
    <div className="flex items-start gap-1">
      {labelNode}
      <span className="flex min-w-0 flex-col font-medium text-content-1000">
        {nameNode}
        <span className="flex min-w-0 items-center gap-1">
          <span className="truncate">
            {address}
            {suffix}
          </span>
          {trailing}
        </span>
      </span>
    </div>
  );
};

const FromField = ({
  address,
  sentViaQuotaFallback,
  tinted
}: {
  address: string;
  sentViaQuotaFallback: boolean;
  tinted?: boolean;
}) => (
  <div className="flex min-w-0 shrink items-center gap-2">
    <AddressField label="From:" address={address} tinted={tinted} />
    {sentViaQuotaFallback ? <QuotaFallbackHint address={address} /> : null}
  </div>
);

const QuotaFallbackHint = ({ address }: { address: string }) => (
  <Tooltip placement="top">
    <TooltipTrigger as="div" className="cursor-pointer">
      <HugeiconsIcon
        icon={AlertCircleIcon}
        size={14}
        className="text-amber-500"
      />
    </TooltipTrigger>
    <TooltipContent className="max-w-[280px] rounded-lg border bg-alpha-800/90 backdrop-blur-sm px-3 py-2 text-xs text-content-1000-inverted shadow-xs">
      Sent from {address} because the connected mailbox reached its daily
      sending limit.
    </TooltipContent>
  </Tooltip>
);

// The card's From/To/Cc/Bcc header. The pop-up (compact) renders the design's
// 11px rows — stacked on the floating card, single "name • address" lines in
// the drawer; /inbox keeps its original block.
const EmailAddresses = ({
  compact,
  inline,
  from,
  to,
  toList,
  ccList,
  bccList,
  sentViaQuotaFallback,
  tinted
}: {
  compact: boolean;
  inline: boolean;
  from?: MessageAddressLite;
  to?: MessageAddressLite;
  toList: string[];
  ccList: string[];
  bccList: string[];
  sentViaQuotaFallback: boolean;
  tinted?: boolean;
}) => {
  const hasAny =
    Boolean(from) ||
    toList.length > 0 ||
    ccList.length > 0 ||
    bccList.length > 0;
  if (!hasAny) return null;

  if (compact)
    return (
      <div className="space-y-1 !border-t-0 pb-3 text-[11px] leading-4">
        <CompactAddressRow
          label="From:"
          name={from?.name}
          address={from?.address}
          inline={inline}
          trailing={
            sentViaQuotaFallback && from ? (
              <QuotaFallbackHint address={from.address} />
            ) : null
          }
        />
        <CompactAddressRow
          label="To:"
          name={to?.name}
          address={toList[0] ?? to?.address}
          overflowCount={Math.max(toList.length - 1, 0)}
          inline={inline}
        />
        <AddressList label="Cc:" addresses={ccList} />
        <AddressList label="Bcc:" addresses={bccList} />
      </div>
    );

  const extraGroups = [
    { label: "To", addresses: toList.slice(1) },
    { label: "Cc", addresses: ccList },
    { label: "Bcc", addresses: bccList }
  ].filter((group) => group.addresses.length > 0);

  const extraCount = extraGroups.reduce(
    (total, group) => total + group.addresses.length,
    0
  );

  return (
    <div className="border-b border-alpha-100 pb-3 pt-4 text-xs">
      <div className="flex min-w-0 flex-nowrap items-center gap-x-4">
        {from ? (
          <FromField
            address={from.address}
            sentViaQuotaFallback={sentViaQuotaFallback}
            tinted={tinted}
          />
        ) : null}
        <Show if={toList.length > 0}>
          <div className="flex min-w-0 shrink items-center gap-2">
            <span className="shrink-0 text-xs font-medium text-content-1000">
              To:
            </span>
            <AddressChip address={toList[0]} tinted={tinted} />
            <Show if={extraCount > 0}>
              <Popover
                placement="bottom-start"
                trigger="hover"
                hoverDelay={{ open: 120, close: 80 }}
              >
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    aria-label={`Show ${extraCount} more ${
                      extraCount === 1 ? "recipient" : "recipients"
                    }`}
                    className="shrink-0 rounded-full px-1.5 py-0.5 text-base font-medium text-content-300 transition-colors hover:bg-alpha-40 hover:text-content-1000"
                  >
                    +{extraCount}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="z-50 max-h-[320px] w-[320px] overflow-y-auto rounded-lg border border-alpha-100 bg-white p-3 shadow-xl">
                  <div className="space-y-3">
                    {extraGroups.map((group) => (
                      <div key={group.label} className="space-y-1.5">
                        <p className="text-xs font-bold text-content-400">
                          {group.label}
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {group.addresses.map((address) => (
                            <AddressChip key={address} address={address} />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </Show>
          </div>
        </Show>
      </div>
    </div>
  );
};

const downloadAttachment = (fileUrl: string, filename: string) => {
  const xhr = new XMLHttpRequest();
  xhr.open("GET", fileUrl, true);
  xhr.responseType = "blob";
  xhr.onload = () => {
    // eslint-disable-next-line compat/compat -- URL.createObjectURL is required for cross-origin downloads; supported in all target browsers
    const url = window.URL.createObjectURL(xhr.response);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    // eslint-disable-next-line compat/compat
    window.URL.revokeObjectURL(url);
  };
  xhr.send();
};

interface MessageAddressLite {
  name: string;
  address: string;
}

interface EmailTitleProps {
  subject?: string;
  isReply: boolean;
  body: string;
  messageId?: string;
  from?: MessageAddressLite;
  onReply?: (target: ReplyTarget) => void;
  compact?: boolean;
}

const EmailTitle = ({
  subject,
  isReply,
  body,
  messageId,
  from,
  onReply,
  compact = false
}: EmailTitleProps) => {
  const canReply = !!(messageId && onReply && from);
  return (
    <div className="flex items-center justify-between gap-3">
      <h3 className="flex items-center gap-2 text-sm font-bold leading-5 text-content-1000">
        {isReply ? (
          <HugeiconsIcon
            icon={LinkBackwardSolidIcon}
            size={20}
            className="flex-shrink-0 text-content-1000"
          />
        ) : null}
        {subject}
      </h3>

      {canReply ? (
        <IconButton
          HugeIcon={LinkBackwardIcon}
          strokeWidth={1.5}
          variant={IconButtonVariant.BASE}
          size={compact ? IconButtonSize.XS : IconButtonSize.SM}
          onClick={() =>
            onReply!({
              messageId: messageId!,
              fromName: from!.name,
              fromAddress: from!.address,
              preview: (body || "").slice(0, 160)
            })
          }
          className="flex-shrink-0"
          aria-label="Reply"
        />
      ) : null}
    </div>
  );
};

interface AttachmentItemProps {
  attachment: Attachment;
}

const AttachmentItem = ({ attachment }: AttachmentItemProps) => (
  <a
    href={attachment.file_url}
    download={attachment.filename}
    onClick={(e) => {
      e.preventDefault();
      downloadAttachment(attachment.file_url, attachment.filename);
    }}
    className="flex items-center gap-3 p-3 rounded-xl border border-alpha-100 hover:bg-background-800 transition-colors"
  >
    <HugeiconsIcon
      icon={Download04Icon}
      size={24}
      className="text-content-300 flex-shrink-0"
    />
    <div className="flex flex-col min-w-0">
      <span className="text-sm font-medium text-content-1000 truncate">
        {attachment.filename}
      </span>
      <span className="text-xs text-content-200">
        File - {formatFileSize(attachment.file_size_bytes)}
      </span>
    </div>
  </a>
);

interface MessageAddress {
  name: string;
  address: string;
}

/* eslint-disable sonarjs/cognitive-complexity -- compact (pop-up) vs full (drawer/inbox) body rendering, each with its own collapse behavior */
const EmailBody = ({
  html,
  plainText,
  subject,
  isCollapsed,
  shouldCollapse,
  onToggle,
  compact = false,
  onCanvasWidth
}: {
  html: string | null;
  plainText: string;
  subject?: string;
  isCollapsed: boolean;
  shouldCollapse: boolean;
  onToggle: () => void;
  /** Pop-up: a collapsed email shows the design's 3-line text preview. */
  compact?: boolean;
  onCanvasWidth: (width: number) => void;
}) => {
  const clampClass = compact && isCollapsed ? "line-clamp-3" : undefined;

  if (compact)
    return (
      <div className={cx("text-sm text-content-1000 break-words pt-3 pb-4")}>
        {html && !isCollapsed ? (
          <EmailFrame
            html={html}
            title={subject ?? "Email body"}
            onCanvasWidth={onCanvasWidth}
            transparentWhenFluid
          />
        ) : (
          <div className={clampClass}>{plainText}</div>
        )}
      </div>
    );

  const inlineToggle = (
    <Show if={shouldCollapse}>
      <button
        type="button"
        onClick={onToggle}
        className="text-sm font-bold text-primary-600"
      >
        {isCollapsed ? "Show more" : "Show less"}
      </button>
    </Show>
  );

  const expandedHtml = isCollapsed ? null : html;
  if (!expandedHtml && !plainText.trim()) return null;

  return (
    <div className="text-sm text-content-1000 [overflow-wrap:anywhere] whitespace-pre-wrap">
      {expandedHtml ? (
        <>
          <EmailFrame
            html={expandedHtml}
            title={subject ?? "Email body"}
            onCanvasWidth={onCanvasWidth}
            transparentWhenFluid
          />
          {inlineToggle}
        </>
      ) : (
        <span className="block">
          {isCollapsed ? (
            <LinkifiedText
              text={`${plainText.slice(0, BODY_COLLAPSE_THRESHOLD).trimEnd()}... `}
              showFullUrl
            />
          ) : (
            <LinkifiedText text={`${plainText} `} showFullUrl />
          )}
          {inlineToggle}
        </span>
      )}
    </div>
  );
};
/* eslint-enable sonarjs/cognitive-complexity */

// Incoming email translates the visible plain-text body (not the HTML frame);
// while showing the translation, render it as plain text and let "Show
// original" restore the HTML view (R12).
const IncomingEmailBody = ({
  preparedHtml,
  plainBodyContent,
  subject,
  isCollapsed,
  shouldCollapse,
  onToggle,
  compact,
  onCanvasWidth,
  translation,
  showTranslated
}: {
  preparedHtml: string | null;
  plainBodyContent: string;
  subject?: string;
  isCollapsed: boolean;
  shouldCollapse: boolean;
  onToggle: () => void;
  compact?: boolean;
  onCanvasWidth: (width: number) => void;
  translation: ReturnType<typeof useMessageTranslation>;
  showTranslated: boolean;
}) => (
  <EmailBody
    html={showTranslated ? null : preparedHtml}
    plainText={
      showTranslated
        ? (translation.translatedText ?? plainBodyContent)
        : plainBodyContent
    }
    subject={subject}
    isCollapsed={showTranslated ? false : isCollapsed}
    shouldCollapse={showTranslated || compact ? false : shouldCollapse}
    onToggle={onToggle}
    compact={compact}
    onCanvasWidth={onCanvasWidth}
  />
);

interface EmailContentProps {
  isFromContact: boolean;
  body: string;
  bodyHtml?: string;
  subject?: string;
  isReply?: boolean;
  sentViaQuotaFallback?: boolean;
  from?: MessageAddress;
  to?: MessageAddress;
  toAddresses?: string[];
  ccAddresses?: string[];
  bccAddresses?: string[];
  messageId?: string;
  onReply?: (target: ReplyTarget) => void;
  attachments?: Attachment[];
  className?: string;
}

export const EmailContent = ({
  isFromContact,
  body,
  bodyHtml,
  subject,
  isReply = false,
  sentViaQuotaFallback = false,
  from,
  to,
  toAddresses,
  ccAddresses,
  bccAddresses,
  messageId,
  onReply,
  attachments,
  className
}: EmailContentProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const embed = useInboxEmbed();
  const isCompact = Boolean(embed?.compact);
  const isDrawerEmbed = embed?.layout === "drawer";

  // Fall back to the single `to` for messages synced before cc/bcc were stored.
  const toFallback = to ? [to.address] : [];
  const toList =
    toAddresses && toAddresses.length > 0 ? toAddresses : toFallback;
  const ccList = ccAddresses ?? [];
  const bccList = bccAddresses ?? [];

  const contentToMeasure = bodyHtml || body;
  const shouldCollapse =
    contentToMeasure.length >
    (isCompact ? COMPACT_COLLAPSE_THRESHOLD : BODY_COLLAPSE_THRESHOLD);

  // EmailFrame sanitizes; only the cid: rewrite needs the attachments.
  const preparedHtml = useMemo(() => {
    if (!bodyHtml) return null;
    return attachments?.length
      ? replaceInlineImages(bodyHtml, attachments)
      : bodyHtml;
  }, [bodyHtml, attachments]);

  const fileAttachments = useMemo(
    () => attachments?.filter((att) => !att.is_inline) ?? [],
    [attachments]
  );

  const [canvasWidth, setCanvasWidth] = useState<number>();

  const translation = useMessageTranslation(body, isFromContact, subject);
  const showTranslated =
    translation.showingTranslation && translation.translatedText !== null;

  const isCollapsed = shouldCollapse && !isExpanded;

  const textBody = useMemo(() => stripMarkupForText(body), [body]);
  let plainBodyContent = textBody;
  // Compact collapse clamps with line-clamp-3 instead of slicing.
  if (isCollapsed && !isCompact)
    plainBodyContent = `${textBody.slice(0, BODY_COLLAPSE_THRESHOLD)}...`;

  const showTranslate =
    isFromContact &&
    body.trim().length > 0 &&
    translation.targetLanguage !== null;
  const showMore = isCompact && shouldCollapse && !showTranslated;

  return (
    <MessageContentCard
      isFromContact={isFromContact}
      contentWidth={canvasWidth}
      className={cx(
        "flex flex-col",
        isCompact ? "divide-y divide-alpha-40 px-5 pb-3" : "gap-3",
        className
      )}
    >
      <EmailTitle
        subject={resolveDisplaySubject(
          subject,
          showTranslated,
          translation.translatedSubject
        )}
        isReply={isReply}
        body={body}
        messageId={messageId}
        from={from}
        onReply={onReply}
        compact={isCompact}
      />

      <EmailAddresses
        compact={isCompact}
        inline={isDrawerEmbed}
        tinted={!isFromContact}
        from={from}
        to={to}
        toList={toList}
        ccList={ccList}
        bccList={bccList}
        sentViaQuotaFallback={sentViaQuotaFallback}
      />

      <IncomingEmailBody
        preparedHtml={preparedHtml}
        plainBodyContent={plainBodyContent}
        subject={subject}
        isCollapsed={isCollapsed}
        shouldCollapse={shouldCollapse}
        onToggle={() => setIsExpanded(!isExpanded)}
        compact={isCompact}
        onCanvasWidth={setCanvasWidth}
        translation={translation}
        showTranslated={showTranslated}
      />

      {fileAttachments.length > 0 && (
        <div className="grid grid-cols-2 gap-3 pt-4">
          {fileAttachments.map((attachment) => (
            <AttachmentItem key={attachment.id} attachment={attachment} />
          ))}
        </div>
      )}

      <Show if={showTranslate || showMore}>
        <div
          className={cx(
            "flex items-center justify-end gap-3",
            isCompact ? "pt-1" : "pt-2"
          )}
        >
          <Show if={showTranslate}>
            <TranslateIncomingToggle
              targetLanguage={translation.targetLanguage ?? "en"}
              showingTranslation={translation.showingTranslation}
              isTranslating={translation.isTranslating}
              onTranslate={translation.translate}
              onShowOriginal={translation.showOriginal}
            />
          </Show>
          <Show if={showMore}>
            <Button
              HugeIcon={isExpanded ? ArrowUp01Icon : ArrowDown01Icon}
              variant={ButtonVariant.TEXT}
              size={ButtonSize.SM}
              className="flex gap-1 items-center !text-primary-600"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? "Show less" : "Show more"}
            </Button>
          </Show>
        </div>
      </Show>
    </MessageContentCard>
  );
};
